import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, Car, Fuel, Phone, Wrench, Utensils, Home, Package, Camera, FileText, Download, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Contractors1099Landing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Free Expense Tracker for 1099 Contractors & Gig Workers | FinScan</title>
        <meta name="description" content="Track every deductible expense automatically. The easiest tax expense tracker for 1099 workers, Uber drivers, DoorDash couriers, and self-employed professionals. Free." />
        <link rel="canonical" href="https://finscan.store/1099" />
        <meta property="og:title" content="Free Expense Tracker for 1099 Contractors & Gig Workers | FinScan" />
        <meta property="og:description" content="Track every deductible expense automatically. Free for 1099 workers, Uber drivers, DoorDash couriers." />
        <meta property="og:url" content="https://finscan.store/1099" />
      </Helmet>

      <div className="min-h-screen bg-[#030712]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2">
                <Receipt className="w-8 h-8 text-indigo-400" />
                <span className="text-2xl font-bold font-['Outfit'] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                  FinScan
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors hidden sm:block">
                  Blog
                </Link>
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-white text-black hover:bg-white/90 font-medium px-4 py-2 rounded-full transition-all"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-green-500/10 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear_gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tight mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-green-200 to-green-400">
                  Never Miss a Tax Deduction Again
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
                Built for 1099 workers. Track every deductible expense automatically — from mileage to phone bills to supplies. <span className="text-green-400 font-semibold">Save hundreds at tax time.</span>
              </p>

              <button
                onClick={() => navigate('/auth')}
                data-testid="1099-cta-hero"
                className="bg-green-500 text-black hover:bg-green-400 font-semibold px-8 py-4 rounded-full shadow-[0_0_30px_rgba(34,197,94,0.4)] transition-all transform hover:scale-105 active:scale-95 text-lg"
              >
                Start Tracking Free
              </button>
            </motion.div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-12 border-y border-white/10 bg-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl mb-2">💸</div>
                <p className="text-lg text-white">Average 1099 worker misses <span className="text-green-400 font-bold">$2,000+</span> in deductions per year</p>
              </div>
              <div>
                <div className="text-3xl mb-2">📱</div>
                <p className="text-lg text-white">Takes <span className="text-green-400 font-bold">30 seconds</span> to log an expense</p>
              </div>
              <div>
                <div className="text-3xl mb-2">🧾</div>
                <p className="text-lg text-white"><span className="text-green-400 font-bold">AI reads</span> your receipts automatically</p>
              </div>
            </div>
          </div>
        </section>

        {/* What You Can Track */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              What You Can Track
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Car, label: 'Mileage (auto-calculated)', color: 'text-blue-400' },
                { icon: Fuel, label: 'Gas & fuel', color: 'text-orange-400' },
                { icon: Phone, label: 'Phone & internet', color: 'text-purple-400' },
                { icon: Wrench, label: 'Tools & supplies', color: 'text-yellow-400' },
                { icon: Utensils, label: 'Business meals', color: 'text-red-400' },
                { icon: Home, label: 'Home office', color: 'text-cyan-400' },
                { icon: Package, label: 'Delivery supplies', color: 'text-pink-400' },
                { icon: Receipt, label: 'All receipts', color: 'text-green-400' },
              ].map(({ icon: Icon, label, color }, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
                >
                  <Icon className={`w-6 h-6 ${color}`} />
                  <span className="text-sm md:text-base">{label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why 1099 Workers Love FinScan */}
        <section className="py-20 bg-gradient-to-b from-transparent to-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              Why 1099 Workers Love FinScan
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl"
              >
                <Camera className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Snap receipts instantly</h3>
                <p className="text-muted-foreground">
                  Point your phone at any receipt. FinScan reads the amount, date, and vendor automatically. No more manual entry.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl"
              >
                <FileText className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Organized by category</h3>
                <p className="text-muted-foreground">
                  IRS-friendly expense categories built in. Your accountant will thank you.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl"
              >
                <Download className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Export in one click</h3>
                <p className="text-muted-foreground">
                  Tax season? Export a complete expense report as PDF or spreadsheet. Done.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-semibold mb-2 text-green-400">I'm a DoorDash / Uber driver. Is this useful for me?</h3>
                <p className="text-muted-foreground">
                  Very much so. Track your mileage, phone expenses, supplies, and any other business costs. At tax time, you'll have everything organized to maximize your Schedule C deductions.
                </p>
              </div>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-semibold mb-2 text-green-400">I'm not good with apps. Is FinScan easy to use?</h3>
                <p className="text-muted-foreground">
                  If you can take a photo, you can use FinScan. Point your camera at a receipt — the app does the rest.
                </p>
              </div>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-semibold mb-2 text-green-400">How much does it cost?</h3>
                <p className="text-muted-foreground">
                  Free for personal use. Pro plan unlocks unlimited history, advanced reports, and team features.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-gradient-to-b from-white/5 to-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-6">
              Start saving money at tax time — it's free
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of 1099 contractors who track smarter.
            </p>
            <button
              onClick={() => navigate('/auth')}
              data-testid="1099-cta-final"
              className="bg-green-500 text-black hover:bg-green-400 font-semibold px-10 py-5 rounded-full shadow-[0_0_30px_rgba(34,197,94,0.4)] transition-all transform hover:scale-105 text-xl"
            >
              Create Your Free Account
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm">
                © 2026 FinScan. Built for independent workers.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/electricians" className="text-muted-foreground hover:text-white transition-colors">Contractors</Link>
                <Link to="/freelancers" className="text-muted-foreground hover:text-white transition-colors">Freelancers</Link>
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors">Blog</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Contractors1099Landing;
